import { useCallback } from "react";

import {
  CreateUserEventRequest,
  UserEventContext,
  UserEventType,
  createUserEvent
} from "api/userEvents";

import { useDashboardContext } from "components/dashboard/hooks";
import { useProjectContext } from "components/project/projects/context";
import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

/**
 * Custom hook for sending user events
 * Provides a convenient interface for tracking user interactions
 */
export function useUserEvents() {
  const { selectedProjectId } = useProjectContext();
  const { workspaceId } = useWorkspaceContext();
  const { dashboard } = useDashboardContext();

  /**
   * Send a user event to the analytics service
   * This function will not throw errors to prevent disrupting user experience
   */
  const sendEvent = useCallback(
    async (eventType: UserEventType | string, context?: UserEventContext) => {
      try {
        const eventData: CreateUserEventRequest = {
          eventType,
          context: {
            projectId: selectedProjectId,
            workspaceId,
            dashboardId: dashboard?.dashboardId,
            ...context
          }
        };

        await createUserEvent(eventData);
      } catch (error) {
        // Silently handle errors - user events should not disrupt user experience
        // The createUserEvent function already handles logging in development
      }
    },
    [selectedProjectId, workspaceId, dashboard?.dashboardId]
  );

  /**
   * Send a screenshot event with comprehensive metadata
   */
  const sendScreenshotEvent = useCallback(
    async (context: UserEventContext) => {
      await sendEvent(UserEventType.Screenshot, context);
    },
    [sendEvent]
  );

  /**
   * Send an export chart data event
   */
  const sendExportChartDataEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ExportChartData, context);
    },
    [sendEvent]
  );

  /**
   * Send a copy data table event
   */
  const sendCopyDataTableEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.CopyDataTable, context);
    },
    [sendEvent]
  );

  /**
   * Send an export data table event
   */
  const sendExportDataTableEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ExportDataTable, context);
    },
    [sendEvent]
  );

  /**
   * Send an export forecasts event
   */
  const sendExportForecastsEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ExportForecasts, context);
    },
    [sendEvent]
  );
  /**
   * Send an change in group by from group by activity
   */
  const sendGlobalGroupByChangedEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.GroupBy, context);
    },
    [sendEvent]
  );

  /**
   * Sends an chart field change
   */
  const sendChartAxisFieldSelectorEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ChartAxisFieldSelector, context);
    },
    [sendEvent]
  );

  /**
   * Sends an chart focus change
   */
  const sendChartFocusSetEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ChartFocus, context);
    },
    [sendEvent]
  );

  /**
   * Sends an chart data source change
   */
  const sendChartDataSourceEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ChartDataSource, context);
    },
    [sendEvent]
  );

  /**
   * Sends an chart time step change
   */
  const sendChartTimeStepEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ChartTimeStep, context);
    },
    [sendEvent]
  );

  /**
   * Sends an chart response to global groupby
   */
  const sendChartGlobalGroupByEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ChartGlobalGroupByChange, context);
    },
    [sendEvent]
  );

  /**
   * Sends an chart product change
   */
  const sendChartProductEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ChartProduct, context);
    },
    [sendEvent]
  );

  /**
   * Sends a change in chart type
   */
  const sendChartTypeOpenedEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.ChartOpened, context);
    },
    [sendEvent]
  );

  /**
   * Sends a change in tab for the ticket
   */
  const sendWellTicketTabChangeEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.WellTicketTab, context);
    },
    [sendEvent]
  );

  /**
   * Sends the well card being pointed at a well, whether that opened the card
   * or retargeted an open one. `source` says which control did it.
   */
  const sendWellCardViewedEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.WellCardViewed, context);
    },
    [sendEvent]
  );

  /**
   * Sends a button toggled on from the midstream navigator toolbar
   */
  const sendMidstreamNavigatorButtonToggledEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.MidstreamNavigatorButtonToggled, context);
    },
    [sendEvent]
  );

  /**
   * Sends a change in filter due to the sales facilities filter button
   */
  const sendSalesFacilitiesFilterEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.SalesFacilitiesFilter, context);
    },
    [sendEvent]
  );

  /**
   * Sends an well network
   */
  const sendWellNetworkShowingEvent = useCallback(
    async (context?: UserEventContext) => {
      await sendEvent(UserEventType.WellNetworkMapLayer, context);
    },
    [sendEvent]
  );

  return {
    sendEvent,
    sendScreenshotEvent,
    sendExportChartDataEvent,
    sendCopyDataTableEvent,
    sendExportDataTableEvent,
    sendExportForecastsEvent,
    sendGlobalGroupByChangedEvent,
    sendChartAxisFieldSelectorEvent,
    sendChartFocusSetEvent,
    sendChartDataSourceEvent,
    sendChartTimeStepEvent,
    sendChartGlobalGroupByEvent,
    sendChartProductEvent,
    sendChartTypeOpenedEvent,
    sendWellTicketTabChangeEvent,
    sendWellCardViewedEvent,
    sendMidstreamNavigatorButtonToggledEvent,
    sendSalesFacilitiesFilterEvent,
    sendWellNetworkShowingEvent
  };
}
